import {
  ChangeDetectionStrategy,
  Component,
  inject,
  signal,
  OnInit,
  computed,
} from '@angular/core';
import { CommonModule } from '@angular/common';
import { Router, ActivatedRoute } from '@angular/router';
import { ButtonModule } from 'primeng/button';
import { TagModule } from 'primeng/tag';
import { DividerModule } from 'primeng/divider';
import { TooltipModule } from 'primeng/tooltip';
import { TranslateModule, TranslateService } from '@ngx-translate/core';
import { ReservedInventoryItem } from '../../reserved-inventory.component';
import {
  AddFormComponent,
  FormField,
  ActionButton,
} from '@shared/components/add-form/add-form.component';
import type { ButtonSeverity } from 'primeng/button';

@Component({
  selector: 'app-reserved-inventory-detail',
  standalone: true,
  imports: [
    CommonModule,
    ButtonModule,
    TagModule,
    DividerModule,
    TooltipModule,
    AddFormComponent,
    TranslateModule,
  ],
  templateUrl: './detail.component.html',
  styleUrl: './detail.component.css',
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class ReservedInventoryDetailComponent implements OnInit {
  private readonly router = inject(Router);
  private readonly route = inject(ActivatedRoute);
  private readonly translateService = inject(TranslateService);

  reservationDetail = signal<ReservedInventoryItem | null>(null);
  isLoading = signal(true);

  // Form configuration
  formFields = computed<FormField[]>(() => [
    // Basic Information Section
    {
      label: 'reserved_inventory_management.detail.fields.reservation_id',
      controlName: 'id',
      type: 'text',
      section:
        'reserved_inventory_management.detail.sections.basic_information',
      readonly: true,
      disabled: true,
    },
    {
      label: 'reserved_inventory_management.detail.fields.product_name',
      controlName: 'spuName',
      type: 'text',
      section:
        'reserved_inventory_management.detail.sections.basic_information',
      readonly: true,
      disabled: true,
    },
    {
      label: 'reserved_inventory_management.detail.fields.product_code',
      controlName: 'spuCode',
      type: 'text',
      section:
        'reserved_inventory_management.detail.sections.basic_information',
      readonly: true,
      disabled: true,
    },
    {
      label: 'reserved_inventory_management.detail.fields.sku_code',
      controlName: 'skuCode',
      type: 'text',
      section:
        'reserved_inventory_management.detail.sections.basic_information',
      readonly: true,
      disabled: true,
    },
    {
      label: 'reserved_inventory_management.detail.fields.status',
      controlName: 'status',
      type: 'text',
      section:
        'reserved_inventory_management.detail.sections.basic_information',
      readonly: true,
      disabled: true,
    },
    {
      label: 'reserved_inventory_management.detail.fields.reservation_date',
      controlName: 'reservationDate',
      type: 'text',
      section:
        'reserved_inventory_management.detail.sections.basic_information',
      readonly: true,
      disabled: true,
    },

    // Warehouse Information Section
    {
      label: 'reserved_inventory_management.detail.fields.warehouse_code',
      controlName: 'warehouseCode',
      type: 'text',
      section:
        'reserved_inventory_management.detail.sections.warehouse_information',
      readonly: true,
      disabled: true,
    },
    {
      label: 'reserved_inventory_management.detail.fields.warehouse_name',
      controlName: 'warehouseName',
      type: 'text',
      section:
        'reserved_inventory_management.detail.sections.warehouse_information',
      readonly: true,
      disabled: true,
    },
    {
      label: 'reserved_inventory_management.detail.fields.order_number',
      controlName: 'orderNumber',
      type: 'text',
      section:
        'reserved_inventory_management.detail.sections.warehouse_information',
      readonly: true,
      disabled: true,
    },
    {
      label: 'reserved_inventory_management.detail.fields.customer_name',
      controlName: 'customerName',
      type: 'text',
      section:
        'reserved_inventory_management.detail.sections.warehouse_information',
      readonly: true,
      disabled: true,
    },

    // Inventory Details Section
    {
      label: 'reserved_inventory_management.detail.fields.reserved_quantity',
      controlName: 'reservedQuantity',
      type: 'number',
      section:
        'reserved_inventory_management.detail.sections.inventory_details',
      readonly: true,
      disabled: true,
    },
    {
      label: 'reserved_inventory_management.detail.fields.available_quantity',
      controlName: 'availableQuantity',
      type: 'number',
      section:
        'reserved_inventory_management.detail.sections.inventory_details',
      readonly: true,
      disabled: true,
    },
    {
      label:
        'reserved_inventory_management.detail.fields.reservation_percentage',
      controlName: 'reservationPercentage',
      type: 'text',
      section:
        'reserved_inventory_management.detail.sections.inventory_details',
      readonly: true,
      disabled: true,
    },
  ]);

  formData = computed(() => {
    const data = this.reservationDetail();
    if (!data) return {};

    return {
      id: `#${data.id}`,
      spuName: data.spuName,
      spuCode: data.spuCode,
      skuCode: data.skuCode,
      status: this.formatReservationStatus(data.status),
      reservationDate: this.formatDate(data.reservationDate || ''),
      warehouseCode: data.warehouseCode,
      warehouseName: data.warehouseName,
      orderNumber: data.orderNumber,
      customerName: data.customerName,
      reservedQuantity: this.formatQuantity(data.reservedQuantity || 0),
      availableQuantity: this.formatQuantity(data.availableQuantity || 0),
      reservationPercentage: `${this.calculateReservationPercentage()}% (${this.translateService.instant(
        'reserved_inventory_management.detail.values.reservation_percentage_formula'
      )})`,
    };
  });

  headerActions = computed<ActionButton[]>(() => [
    {
      label: 'reserved_inventory_management.detail.actions.return',
      action: 'return',
      severity: 'secondary' as ButtonSeverity,
      outlined: true,
    },
    ...(this.canAdjust()
      ? [
          {
            label:
              'reserved_inventory_management.detail.actions.adjust_quantity',
            action: 'adjust',
            severity: 'secondary' as ButtonSeverity,
          },
        ]
      : []),
    ...(this.canRelease()
      ? [
          {
            label:
              'reserved_inventory_management.detail.actions.release_reservation',
            action: 'release',
            severity: 'success' as ButtonSeverity,
          },
        ]
      : []),
  ]);

  ngOnInit(): void {
    this.loadReservationDetail();
  }

  private loadReservationDetail(): void {
    this.route.params.subscribe((params) => {
      const reservationId = params['id'];

      // Mock data - replace with actual service call
      const mockData: ReservedInventoryItem = {
        id: parseInt(reservationId),
        spuCode: 'SPU-001',
        spuName: 'iPhone 15 Pro Max',
        skuCode: 'SKU-001-256GB-TITANIUM',
        warehouseCode: 'WH-001',
        warehouseName: 'Main Warehouse',
        reservedQuantity: 50,
        availableQuantity: 200,
        orderNumber: 'ORD-2024-001',
        customerName: 'John Doe',
        reservationDate: '2024-01-15T10:30:00Z',
        status: 'Reserved',
        isOverReserved: false,
        hasConflict: false,
      };

      // Simulate loading delay
      setTimeout(() => {
        this.reservationDetail.set(mockData);
        this.isLoading.set(false);
      }, 500);
    });
  }

  onHeaderAction(event: any): void {
    switch (event.action) {
      case 'return':
        this.onReturn();
        break;
      case 'release':
        this.onRelease();
        break;
      case 'adjust':
        this.onAdjust();
        break;
    }
  }

  onReturn(): void {
    this.router.navigate(['/stock/reserved-inventory']);
  }

  onRelease(): void {
    // Handle release action
    console.log('Release reservation:', this.reservationDetail());
  }

  onAdjust(): void {
    // Handle adjust action
    console.log('Adjust reservation:', this.reservationDetail());
  }

  // Status styling
  getStatusSeverity(status: string): string {
    switch (status) {
      case 'Reserved':
        return 'info';
      case 'Released':
        return 'success';
      case 'Pending':
        return 'warn';
      case 'Over-reserved':
        return 'danger';
      default:
        return 'info';
    }
  }

  getStatusClass(status: string): string {
    switch (status) {
      case 'Reserved':
        return 'status-badge reserved';
      case 'Released':
        return 'status-badge released';
      case 'Pending':
        return 'status-badge pending';
      case 'Over-reserved':
        return 'status-badge over-reserved';
      default:
        return 'status-badge';
    }
  }

  formatDate(dateString: string): string {
    const date = new Date(dateString);
    return date.toLocaleDateString('en-GB', {
      year: 'numeric',
      month: '2-digit',
      day: '2-digit',
      hour: '2-digit',
      minute: '2-digit',
      second: '2-digit',
      hour12: false,
    });
  }

  formatQuantity(quantity: number): string {
    return quantity.toLocaleString();
  }

  calculateReservationPercentage(): number {
    const data = this.reservationDetail();
    if (!data) return 0;
    const total = data.availableQuantity + data.reservedQuantity;
    return total > 0 ? Math.round((data.reservedQuantity / total) * 100) : 0;
  }

  formatReservationStatus(status: string): string {
    switch (status) {
      case 'Reserved':
        return this.translateService.instant(
          'reserved_inventory_management.status.reserved'
        );
      case 'Released':
        return this.translateService.instant(
          'reserved_inventory_management.status.released'
        );
      case 'Pending':
        return this.translateService.instant(
          'reserved_inventory_management.status.pending'
        );
      case 'Over-reserved':
        return this.translateService.instant(
          'reserved_inventory_management.status.over_reserved'
        );
      default:
        return status;
    }
  }

  canRelease(): boolean {
    const data = this.reservationDetail();
    return data?.status === 'Reserved' || data?.status === 'Pending';
  }

  canAdjust(): boolean {
    const data = this.reservationDetail();
    return data?.status === 'Reserved' || data?.status === 'Pending';
  }
}
